import { capitalize } from "lodash-es";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetOrganisations } from "@/features/organisations/api/get-organisations";
import { useAddRoomSystem } from "@/features/room-systems/api/add-room-system";
import {
  RoomSystemFormData,
  createRoomSystemSchema,
} from "@/features/room-systems/types/add-room-system-schema";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { HttpError, isBadRequest } from "@/types/error";
import {
  PublicRoomSystem,
  RoomSystemType,
  RoomSystemVisibility,
} from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateRoomSystem } from "../api/update-room-system";

type RoomSystemFormProps = {
  initialData?: PublicRoomSystem;
};

function apiDataToFormData({
  data,
  organisationId,
}: {
  data?: PublicRoomSystem;
  organisationId: string;
}): RoomSystemFormData {
  if (!data) {
    return {
      organisationId: organisationId ?? "",
      name: "",
      displayName: "",
      extension: import.meta.env.VITE_TENANT_PREFIX,
      email: "",
      password: "",
      type: RoomSystemType.CLASSIC,
      visibility: RoomSystemVisibility.PRIVATE,
    };
  }

  return {
    organisationId: data.organisation.id,
    name: data.name,
    displayName: data.displayName,
    extension: data.extension,
    email: data.email,
    password: "",
    type: data.type,
    visibility: data.visibility,
  };
}

export function RoomSystemForm(props: RoomSystemFormProps) {
  const { initialData } = props;
  const navigate = useNavigate();
  const isEdit = !!initialData;
  const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { organisation } = useOrganisation();
  const form = useForm<RoomSystemFormData>({
    resolver: zodResolver(
      createRoomSystemSchema({ isEdit, isPasswordUpdating }),
    ),
    defaultValues: apiDataToFormData({
      data: initialData,
      organisationId: organisation.id,
    }),
  });
  const { mutateAsync: addRoomSystem, isSuccess } = useAddRoomSystem({
    onSuccess: (roomSystem) => {
      navigate(`/admin/room-systems/${roomSystem.id}`);
    },
  });

  const { mutateAsync: updateRoomSystem } = useUpdateRoomSystem({
    onSuccess: (roomSystem) => {
      form.reset(
        apiDataToFormData({
          data: roomSystem,
          organisationId: roomSystem.organisation.id,
        }),
      );
    },
  });

  const { data: organisations, isPending: isLoadingOrganisations } =
    useGetOrganisations();

  async function handleAddRoomSystem(data: RoomSystemFormData) {
    try {
      const { organisationId, ...roomSystemData } = data;
      await addRoomSystem({ organisationId, data: roomSystemData });
    } catch (error) {
      const httpError = error as HttpError;

      if (isBadRequest(httpError)) {
        const errors = httpError.message;

        errors.forEach((error) => {
          if (
            error.includes("Invalid Extension") ||
            error.includes("already exists with extension")
          ) {
            form.setError("extension", {
              message: error,
            });
          }

          if (error.includes("already exists with name")) {
            form.setError("name", {
              message: error,
            });
          }
        });

        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  async function handleUpdateRoomSystem(data: RoomSystemFormData) {
    if (!isEdit) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { organisationId, password, name, ...roomSystemData } = data;
    try {
      await updateRoomSystem({
        id: initialData?.id,
        organisationId: organisationId,
        roomSystem: {
          ...roomSystemData,
          // dont add password unless updating
          ...(isPasswordUpdating ? { password } : {}),
        },
      });
      setIsPasswordUpdating(false);
    } catch (error) {
      const httpError = error as HttpError;

      if (isBadRequest(httpError)) {
        const errors = httpError.message;

        errors.forEach((error) => {
          if (
            error.includes("Invalid Extension") ||
            error.includes("already exists with extension")
          ) {
            form.setError("extension", {
              message: error,
            });
          }
        });

        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={
          isEdit
            ? form.handleSubmit(handleUpdateRoomSystem)
            : form.handleSubmit(handleAddRoomSystem)
        }
      >
        <Card>
          <Card.Header title="Room System Details" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="organisationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Organisation</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isLoadingOrganisations || isEdit}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an organisation..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {organisations?.map((organisation) => (
                        <SelectItem
                          key={organisation.id}
                          value={organisation.id}
                        >
                          {organisation.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Room System Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a room system type..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(RoomSystemType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {capitalize(type.replace("_", " "))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Name</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isEdit} autoComplete="off" />
                  </FormControl>
                  <FormDescription>
                    Name your room system after the room it's operating in, e.g.
                    Room 101, Room of Requirement, The War Room etc
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Display Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormDescription>
                    How your room system is displayed to users.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isEdit ? (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isRequired={isPasswordUpdating}>
                      {isPasswordUpdating ? "New Password" : "Password"}
                    </FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        <div className="relative flex flex-1">
                          <Input
                            type={isPasswordVisible ? "text" : "password"}
                            placeholder="••••••"
                            {...field}
                            className="pr-8"
                            disabled={!isPasswordUpdating}
                            autoComplete="off"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            type="button"
                            className="absolute right-0 top-1/2 -translate-y-1/2 hover:bg-transparent"
                            disabled={!isPasswordUpdating && isEdit}
                            aria-pressed={isPasswordVisible}
                            aria-label="Toggle password visibility"
                            onClick={() =>
                              setIsPasswordVisible(!isPasswordVisible)
                            }
                          >
                            {isPasswordVisible ? (
                              <EyeOff
                                className="size-4"
                                aria-label="Hide password"
                              />
                            ) : (
                              <Eye
                                className="size-4"
                                aria-label="Show password"
                              />
                            )}
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          type="button"
                          onClick={() =>
                            setIsPasswordUpdating(!isPasswordUpdating)
                          }
                        >
                          {isPasswordUpdating ? "Cancel" : "Update Password"}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isRequired>Password</FormLabel>
                    <FormControl>
                      <div className="relative flex">
                        <Input
                          type={isPasswordVisible ? "text" : "password"}
                          placeholder="••••••"
                          {...field}
                          className="pr-8"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          className="absolute right-0 top-1/2 -translate-y-1/2 hover:bg-transparent"
                          onClick={() =>
                            setIsPasswordVisible(!isPasswordVisible)
                          }
                        >
                          {isPasswordVisible ? (
                            <EyeOff
                              className="size-4"
                              aria-label="Hide password"
                            />
                          ) : (
                            <Eye
                              className="size-4"
                              aria-label="Show password"
                            />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="visibility"
              render={({ field }) => (
                <FormItem className="space-y-3 rounded-md border border-border px-3 py-2">
                  <FormLabel>Privacy Level</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <FormItem className="flex space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="PRIVATE" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Private
                          <span className="block text-sm text-muted-foreground">
                            Available to your organisation and group only
                          </span>
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="PUBLIC" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Public
                          <span className="block text-sm text-muted-foreground">
                            Available to the entire ClassView network
                          </span>
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="extension"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Extension Number</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormDescription>
                    Your extension prefix should be{" "}
                    {import.meta.env.VITE_TENANT_PREFIX}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Email</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormDescription>
                    Notifications for this Room System will be sent to this
                    email address
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <Card.Footer className="flex justify-end">
            <Button
              type="submit"
              isLoading={form.formState.isSubmitting || isSuccess}
              isAnimated
              disabled={!form.formState.isDirty || form.formState.isSubmitting}
            >
              {isEdit ? "Update Room System" : "Add Room System"}
            </Button>
          </Card.Footer>
        </Card>
      </form>
    </Form>
  );
}
